// 상품 신규 등록 + 수정 공용 폼.
// URL 파라미터 :id 가 "new" 면 신규, 그 외는 해당 ID 의 기존 상품 로드 후 수정 모드.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  IMAGE_UPLOAD_KIND,
  PRODUCT_OPTION_MAX,
  PRODUCT_UNIT,
  type ProductUnit,
} from "@oo/shared";
import {
  createProduct,
  getProductAdmin,
  listCategoriesAdmin,
  listUnitsAdmin,
  updateProduct,
} from "@/features/catalog/catalogApi.ts";
import { Button } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import ImageUploader from "@/features/catalog/ImageUploader.tsx";

// 추가 옵션 입력 행. 숫자도 입력 단계에서는 문자열로 관리하고 제출 시 변환한다(폼 전체 관례).
interface OptionFormRow {
  label: string;
  addGrams: string;
  addPriceKrw: string;
}

interface FormState {
  // 대표(기본) 카테고리.
  categoryId: string;
  // 노출되는 전체 카테고리 집합(대표 포함, 개선항목 11).
  categoryIds: string[];
  name: string;
  priceKrw: string; // 입력 단계에서는 문자열로 관리하고 제출 시 변환
  unit: ProductUnit;
  // 단위가 '팩'일 때 1팩 중량(g). 문자열로 관리하고 제출 시 변환(빈 값=미설정).
  packWeightG: string;
  // 실중량 정산 대상 여부(PR-B). 무게가변 단위 선택 시 자동 체크되며 관리자가 해제할 수 있다.
  weightAdjustable: boolean;
  origin: string;
  description: string;
  mainImageKey: string | null;
  detailImageKeys: string[];
  // 추가 옵션(예: "100g 추가" / 100g / 3,600원). 손님이 담을 때 개수를 고른다.
  options: OptionFormRow[];
  isVisible: boolean;
  isSoldOut: boolean;
  isBest: boolean;
}

const DEFAULT_STATE: FormState = {
  categoryId: "",
  categoryIds: [],
  name: "",
  priceKrw: "",
  unit: PRODUCT_UNIT.G100,
  packWeightG: "",
  // 기본 단위(100g)는 무게가변이므로 기본 체크.
  weightAdjustable: true,
  origin: "",
  description: "",
  mainImageKey: null,
  detailImageKeys: [],
  options: [],
  isVisible: true,
  isSoldOut: false,
  isBest: false,
};

// 상품 생성/수정 화면.
export default function ProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [state, setState] = useState<FormState>(DEFAULT_STATE);
  const [error, setError] = useState<string | null>(null);

  const { data: categories = [] } = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: listCategoriesAdmin,
  });

  // 단위 목록(DB 관리). 드롭다운에는 활성 단위만 노출하되, 수정 중인 상품의 현재 단위가
  // 비활성이어도 선택지가 사라지지 않도록 현재 단위는 항상 포함한다.
  const { data: units = [] } = useQuery({
    queryKey: ["admin", "units"],
    queryFn: listUnitsAdmin,
  });
  const unitOptions = units.filter(
    (u) => u.isActive || u.code === state.unit,
  );

  const { data: existing } = useQuery({
    queryKey: ["admin", "product", id],
    queryFn: () => getProductAdmin(id!),
    enabled: !isNew && !!id,
  });

  // 기존 상품 로드 시 폼 초기화.
  useEffect(() => {
    if (existing) {
      setState({
        categoryId: existing.categoryId,
        categoryIds:
          existing.categoryIds && existing.categoryIds.length > 0
            ? existing.categoryIds
            : [existing.categoryId],
        name: existing.name,
        priceKrw: String(existing.priceKrw),
        unit: existing.unit as ProductUnit,
        packWeightG: existing.packWeightG != null ? String(existing.packWeightG) : "",
        weightAdjustable: existing.weightAdjustable,
        origin: existing.origin ?? "",
        description: existing.description ?? "",
        mainImageKey: existing.mainImageKey,
        detailImageKeys: existing.detailImageKeys,
        options: (existing.options ?? []).map((o) => ({
          label: o.label,
          addGrams: String(o.addGrams),
          addPriceKrw: String(o.addPriceKrw),
        })),
        isVisible: existing.isVisible,
        isSoldOut: existing.isSoldOut,
        isBest: existing.isBest,
      });
    }
  }, [existing]);

  // 신규 등록 시 대표 카테고리를 자동으로 고정하지 않는다(개선 요청).
  // 첫 카테고리를 기본 선택하던 동작을 제거해, 사용자가 직접 원하는 카테고리를 고르게 한다.
  // 미선택 상태는 저장 시 검증(아래 saveMut)에서 막는다.

  const saveMut = useMutation({
    mutationFn: async () => {
      const price = Number.parseInt(state.priceKrw, 10);
      if (!state.categoryId) throw new Error("카테고리를 선택하세요.");
      if (!state.name.trim()) throw new Error("상품명을 입력하세요.");
      if (!Number.isFinite(price) || price < 0) throw new Error("가격을 0 이상 정수로 입력하세요.");
      // 팩 단위일 때만 중량을 반영. 빈 값/0 은 null(미설정). 양의 정수 검증.
      let packWeightG: number | null = null;
      if (state.unit === PRODUCT_UNIT.PACK && state.packWeightG.trim()) {
        const w = Number.parseInt(state.packWeightG, 10);
        if (!Number.isFinite(w) || w <= 0) throw new Error("팩 중량(g)을 1 이상 정수로 입력하세요.");
        packWeightG = w;
      }
      // 추가 옵션 검증/정규화. 빈 행(문구 미입력)은 저장에서 제외한다.
      const options = state.options
        .filter((o) => o.label.trim().length > 0)
        .map((o) => {
          const grams = o.addGrams.trim() ? Number.parseInt(o.addGrams, 10) : 0;
          const price = o.addPriceKrw.trim() ? Number.parseInt(o.addPriceKrw, 10) : 0;
          if (!Number.isFinite(grams) || grams < 0) {
            throw new Error(`추가 옵션 "${o.label.trim()}" 의 중량(g)을 0 이상 정수로 입력하세요.`);
          }
          if (!Number.isFinite(price) || price < 0) {
            throw new Error(`추가 옵션 "${o.label.trim()}" 의 금액(원)을 0 이상 정수로 입력하세요.`);
          }
          return { label: o.label.trim(), addGrams: grams, addPriceKrw: price };
        });
      const payload = {
        categoryId: state.categoryId,
        // 대표 카테고리는 항상 멤버십에 포함되도록 합쳐서 보낸다.
        categoryIds: Array.from(new Set([state.categoryId, ...state.categoryIds])),
        name: state.name.trim(),
        priceKrw: price,
        unit: state.unit,
        packWeightG,
        weightAdjustable: state.weightAdjustable,
        origin: state.origin.trim() || null,
        description: state.description.trim() || null,
        mainImageKey: state.mainImageKey,
        detailImageKeys: state.detailImageKeys,
        options,
        isVisible: state.isVisible,
        isSoldOut: state.isSoldOut,
        isBest: state.isBest,
      };
      if (isNew) {
        await createProduct(payload);
      } else {
        await updateProduct(id!, payload);
      }
    },
    onSuccess: () => navigate("/catalog/products", { replace: true }),
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "저장 실패"),
  });

  function addDetailImage(key: string) {
    setState((s) => ({ ...s, detailImageKeys: [...s.detailImageKeys, key] }));
  }
  function removeDetailImage(idx: number) {
    setState((s) => ({ ...s, detailImageKeys: s.detailImageKeys.filter((_, i) => i !== idx) }));
  }
  function moveDetailImage(idx: number, dir: -1 | 1) {
    setState((s) => {
      const next = [...s.detailImageKeys];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return s;
      const [m] = next.splice(idx, 1);
      if (!m) return s;
      next.splice(target, 0, m);
      return { ...s, detailImageKeys: next };
    });
  }

  // 추가 옵션 행 추가/수정/삭제/순서이동 (상세 이미지 목록과 동일한 패턴).
  function addOption() {
    setState((s) =>
      s.options.length >= PRODUCT_OPTION_MAX
        ? s
        : { ...s, options: [...s.options, { label: "", addGrams: "", addPriceKrw: "" }] },
    );
  }
  function patchOption(idx: number, patch: Partial<OptionFormRow>) {
    setState((s) => ({
      ...s,
      options: s.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  }
  function removeOption(idx: number) {
    setState((s) => ({ ...s, options: s.options.filter((_, i) => i !== idx) }));
  }
  function moveOption(idx: number, dir: -1 | 1) {
    setState((s) => {
      const next = [...s.options];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return s;
      const [m] = next.splice(idx, 1);
      if (!m) return s;
      next.splice(target, 0, m);
      return { ...s, options: next };
    });
  }

  return (
    <div className="mx-auto flex max-w-screen-md flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title={isNew ? "신규 상품 등록" : "상품 수정"}
        actions={
          <Button variant="secondary" size="sm" onClick={() => navigate("/catalog/products")}>
            목록으로
          </Button>
        }
      />

      <section className="flex flex-col gap-4 rounded-md border border-ink-200 bg-white p-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-600">대표 카테고리</span>
          <select
            value={state.categoryId}
            onChange={(e) =>
              setState((s) => {
                const next = e.target.value;
                // 대표를 바꾸면 멤버십 집합에도 반드시 포함되도록 보정.
                const ids = next && !s.categoryIds.includes(next) ? [...s.categoryIds, next] : s.categoryIds;
                return { ...s, categoryId: next, categoryIds: ids };
              })
            }
            className="rounded-md border border-ink-300 px-3 py-2 text-sm"
          >
            <option value="">선택</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>

        {/* 추가 노출 카테고리(개선항목 11). 한 상품을 여러 카테고리에 노출할 수 있다. 대표는 항상 포함. */}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-ink-600">노출 카테고리 (여러 곳 선택 가능)</span>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => {
              const isPrimary = c.id === state.categoryId;
              const checked = isPrimary || state.categoryIds.includes(c.id);
              return (
                <label
                  key={c.id}
                  className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm ${
                    checked ? "border-brand-600 bg-brand-50 text-brand-700" : "border-ink-300 text-ink-700"
                  } ${isPrimary ? "opacity-70" : "cursor-pointer"}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isPrimary}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        categoryIds: e.target.checked
                          ? Array.from(new Set([...s.categoryIds, c.id]))
                          : s.categoryIds.filter((x) => x !== c.id),
                      }))
                    }
                  />
                  {c.name}
                  {isPrimary ? <span className="text-[11px]">(대표)</span> : null}
                </label>
              );
            })}
          </div>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-600">상품명</span>
          <input
            type="text"
            value={state.name}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            maxLength={80}
            className="rounded-md border border-ink-300 px-3 py-2 text-sm"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-600">가격(원)</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={state.priceKrw}
              onChange={(e) => setState((s) => ({ ...s, priceKrw: e.target.value }))}
              className="rounded-md border border-ink-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-600">단위</span>
            <select
              value={state.unit}
              onChange={(e) => {
                // 단위를 바꾸면 그 단위의 무게가변 기본값으로 실중량 정산 체크를 다시 맞춘다(관리자가 이후 해제 가능).
                const code = e.target.value;
                const picked = units.find((u) => u.code === code);
                setState((s) => ({
                  ...s,
                  unit: code as ProductUnit,
                  weightAdjustable: picked ? picked.weightVariable : s.weightAdjustable,
                }));
              }}
              className="rounded-md border border-ink-300 px-3 py-2 text-sm"
            >
              {unitOptions.map((u) => (
                <option key={u.code} value={u.code}>
                  {u.label}
                  {u.isActive ? "" : " (비활성)"}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* 추가 옵션(가격 바로 아래). 손님이 담기 모달에서 개수를 골라 기본 수량과 함께 담는다.
            중량(g)은 실중량 정산에 합산되고, 금액(원)은 줄 합계에 더해진다. */}
        <div className="flex flex-col gap-2 rounded-md border border-ink-200 bg-ink-50 p-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-ink-800">추가 옵션</span>
              <span className="ml-2 text-[11px] text-ink-500">
                예: "100g 추가" / 100 / 3600 (선택 사항)
              </span>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={addOption}
              disabled={state.options.length >= PRODUCT_OPTION_MAX}
            >
              + 옵션 추가
            </Button>
          </div>
          {state.options.length === 0 ? (
            <p className="text-xs text-ink-500">
              등록된 추가 옵션이 없습니다. 옵션을 추가하면 손님이 담을 때 개수를 선택할 수 있습니다.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {state.options.map((opt, idx) => (
                <li
                  key={idx}
                  className="flex flex-col gap-2 rounded-md border border-ink-200 bg-white p-2 sm:flex-row sm:items-end"
                >
                  <label className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-[11px] text-ink-600">문구(손님에게 보임)</span>
                    <input
                      type="text"
                      value={opt.label}
                      onChange={(e) => patchOption(idx, { label: e.target.value })}
                      maxLength={40}
                      placeholder="100g 추가"
                      className="rounded-md border border-ink-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="flex w-full flex-col gap-1 sm:w-28">
                    <span className="text-[11px] text-ink-600">중량(g)</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={opt.addGrams}
                      onChange={(e) => patchOption(idx, { addGrams: e.target.value })}
                      placeholder="100"
                      className="rounded-md border border-ink-300 px-2 py-1.5 text-sm tabular-nums"
                    />
                  </label>
                  <label className="flex w-full flex-col gap-1 sm:w-32">
                    <span className="text-[11px] text-ink-600">금액(원)</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={opt.addPriceKrw}
                      onChange={(e) => patchOption(idx, { addPriceKrw: e.target.value })}
                      placeholder="3600"
                      className="rounded-md border border-ink-300 px-2 py-1.5 text-sm tabular-nums"
                    />
                  </label>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveOption(idx, -1)}
                      disabled={idx === 0}
                      aria-label="위로"
                      className="h-8 w-8 rounded-md border border-ink-300 text-xs text-ink-700 hover:bg-ink-100 disabled:opacity-40"
                    >
                      up
                    </button>
                    <button
                      type="button"
                      onClick={() => moveOption(idx, 1)}
                      disabled={idx === state.options.length - 1}
                      aria-label="아래로"
                      className="h-8 w-8 rounded-md border border-ink-300 text-xs text-ink-700 hover:bg-ink-100 disabled:opacity-40"
                    >
                      dn
                    </button>
                    <button
                      type="button"
                      onClick={() => removeOption(idx)}
                      aria-label="옵션 삭제"
                      className="h-8 rounded-md border border-danger-500/40 px-2 text-xs text-danger-700 hover:bg-danger-50"
                    >
                      삭제
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-ink-500">
            중량이 있는 옵션은 준비완료 때 기본 중량과 합쳐 실중량으로 정산됩니다. 중량이 없는 부가
            옵션이면 0으로 두세요.
          </p>
        </div>

        {/* 실중량 정산 대상 토글(PR-B). 무게가변 단위는 준비완료 때 실제 중량으로 최종가를 확정한다. */}
        <label className="flex items-start gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={state.weightAdjustable}
            onChange={(e) => setState((s) => ({ ...s, weightAdjustable: e.target.checked }))}
            className="mt-0.5"
          />
          <span>
            실중량 정산 대상
            <span className="ml-1 block text-[11px] text-ink-500">
              체크 시 준비완료 단계에서 실제 중량을 입력해 최종 금액을 확정합니다(정육 등 무게가변 상품).
            </span>
          </span>
        </label>
        {/* 단위가 '팩'일 때만 1팩 중량(g) 입력. 입력 시 상품 표시가 "팩(500g)" 형태가 된다. */}
        {state.unit === PRODUCT_UNIT.PACK ? (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-600">1팩 중량(g)</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              value={state.packWeightG}
              onChange={(e) => setState((s) => ({ ...s, packWeightG: e.target.value }))}
              placeholder="예: 500 (비우면 표시 안 함)"
              className="rounded-md border border-ink-300 px-3 py-2 text-sm"
            />
            <span className="text-[11px] text-ink-500">
              입력하면 상품 목록/상세에 "팩({state.packWeightG.trim() || "500"}g)"처럼 표시됩니다.
            </span>
          </label>
        ) : null}
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-600">원산지</span>
          <input
            type="text"
            value={state.origin}
            onChange={(e) => setState((s) => ({ ...s, origin: e.target.value }))}
            maxLength={60}
            placeholder="예: 국내산 한우"
            className="rounded-md border border-ink-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-600">상세 설명</span>
          <textarea
            value={state.description}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            maxLength={2000}
            rows={4}
            className="rounded-md border border-ink-300 px-3 py-2 text-sm"
          />
        </label>
      </section>

      <section className="flex flex-col gap-3 rounded-md border border-ink-200 bg-white p-4">
        <h3 className="text-sm font-semibold">메인 이미지</h3>
        <ImageUploader
          kind={IMAGE_UPLOAD_KIND.PRODUCT_MAIN}
          value={state.mainImageKey}
          onChange={(key) => setState((s) => ({ ...s, mainImageKey: key }))}
          previewClassName="h-40 w-40"
        />
      </section>

      <section className="flex flex-col gap-3 rounded-md border border-ink-200 bg-white p-4">
        <h3 className="text-sm font-semibold">상세 이미지 ({state.detailImageKeys.length}/20)</h3>
        <ul className="flex flex-col gap-2">
          {state.detailImageKeys.map((key, idx) => (
            <li key={`${key}-${idx}`} className="flex items-center gap-3 rounded-md border border-ink-200 p-2">
              <div className="h-16 w-16 overflow-hidden rounded border border-ink-200 bg-ink-50">
                <img src={`${import.meta.env.VITE_API_BASE}/images/${key}`} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="ml-auto flex gap-1 text-xs">
                <button type="button" onClick={() => moveDetailImage(idx, -1)} disabled={idx === 0} className="rounded border border-ink-300 px-2 py-0.5 hover:bg-ink-100 disabled:opacity-40">위로</button>
                <button type="button" onClick={() => moveDetailImage(idx, 1)} disabled={idx === state.detailImageKeys.length - 1} className="rounded border border-ink-300 px-2 py-0.5 hover:bg-ink-100 disabled:opacity-40">아래로</button>
                <button type="button" onClick={() => removeDetailImage(idx)} className="rounded border border-ink-300 px-2 py-0.5 text-danger-700 hover:bg-danger-50">제거</button>
              </div>
            </li>
          ))}
        </ul>
        {state.detailImageKeys.length < 20 ? (
          <ImageUploader
            kind={IMAGE_UPLOAD_KIND.PRODUCT_DETAIL}
            value={null}
            onChange={(key) => { if (key) addDetailImage(key); }}
            previewClassName="h-24 w-24"
          />
        ) : null}
      </section>

      <section className="flex flex-col gap-2 rounded-md border border-ink-200 bg-white p-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={state.isVisible} onChange={(e) => setState((s) => ({ ...s, isVisible: e.target.checked }))} />
          사용자 화면 노출
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={state.isSoldOut} onChange={(e) => setState((s) => ({ ...s, isSoldOut: e.target.checked }))} />
          품절 처리
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={state.isBest} onChange={(e) => setState((s) => ({ ...s, isBest: e.target.checked }))} />
          베스트 상품으로 지정
        </label>
      </section>

      {error ? <p className="text-sm text-danger-600">{error}</p> : null}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate("/catalog/products")}
          className="rounded-md border border-ink-300 px-4 py-2 text-sm hover:bg-ink-100"
        >
          취소
        </button>
        <button
          type="button"
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saveMut.isPending ? "저장 중..." : "저장"}
        </button>
      </div>
    </div>
  );
}
